import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import type { ConditionState, ConditionGroupState } from '@/stores/segmentBuilder.store';
import { ConditionRow } from './ConditionRow';
import { SegmentOperator as SegmentOperatorEnum } from '@/gql/graphql';
import { CirclePlus } from 'lucide-react';
import { StyledSelectTrigger } from './StyledSelectTrigger';
import { OperatorSeparator } from './OperatorSeparator';

interface CriteriaSectionProps {
  groups: ConditionGroupState[];
  onRemoveConditionFromGroup: (groupClientId: string, conditionClientId: string) => void;
  onUpdateConditionInGroup: (
    groupClientId: string,
    conditionClientId: string,
    updatedCondition: Partial<ConditionState>
  ) => void;
  onOpenAddConditionDialog: (groupClientId: string) => void;
  onSetGroupOperator: (groupClientId: string, op: SegmentOperatorEnum) => void;
}

export function CriteriaSection({
  groups,
  onRemoveConditionFromGroup,
  onUpdateConditionInGroup,
  onOpenAddConditionDialog,
  onSetGroupOperator
}: CriteriaSectionProps) {
  if (groups.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-medium">Criteria</CardTitle>
        </CardHeader>
        <CardContent className="min-h-[100px] flex items-center justify-center">
          <p className="text-sm text-muted-foreground">No criteria groups defined yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map((group, groupIndex) => {
        const handleGroupOperatorChange = (value: string) => {
          const newOperator = value === 'or' ? SegmentOperatorEnum.Or : SegmentOperatorEnum.And;
          onSetGroupOperator(group.clientId, newOperator);
        };

        const selectValue = group.operator === SegmentOperatorEnum.Or ? 'or' : 'and';
        const operatorText = group.operator === SegmentOperatorEnum.Or ? 'or' : 'and';

        return (
          <Card key={group.clientId}>
            <CardHeader>
              <CardTitle className="text-lg font-medium">Criteria Group {groupIndex + 1}</CardTitle>
              <div className="flex items-center space-x-2 pt-3 text-sm">
                <span>Subscriber matches</span>
                <Select value={selectValue} onValueChange={handleGroupOperatorChange}>
                  <StyledSelectTrigger className="bg-purple-100 text-purple-700 hover:bg-purple-200">
                    <SelectValue placeholder="Select..." />
                  </StyledSelectTrigger>
                  <SelectContent>
                    <SelectItem value="and">all</SelectItem>
                    <SelectItem value="or">any</SelectItem>
                  </SelectContent>
                </Select>
                <span>of the conditions in this group:</span>
              </div>
            </CardHeader>
            <CardContent className="min-h-[100px] space-y-4 pt-4">
              {group.conditions.length > 0 ? (
                group.conditions.map((condition: ConditionState, conditionIndex: number) => (
                  <React.Fragment key={condition.clientId}>
                    <ConditionRow
                      config={condition}
                      onUpdate={(updatedConfig: Partial<ConditionState>) => {
                        onUpdateConditionInGroup(group.clientId, condition.clientId, updatedConfig);
                      }}
                      onDelete={() => {
                        onRemoveConditionFromGroup(group.clientId, condition.clientId);
                      }}
                    />
                    {conditionIndex < group.conditions.length - 1 && (
                      <OperatorSeparator text={operatorText} colorClass="sky-500" />
                    )}
                  </React.Fragment>
                ))
              ) : (
                <div className="flex items-center justify-center h-full min-h-[60px]">
                  <p className="text-sm text-muted-foreground">
                    No conditions defined in this group. Add one below.
                  </p>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex-col items-start gap-4">
              <Button
                variant="dotted"
                size="sm"
                onClick={() => {
                  onOpenAddConditionDialog(group.clientId);
                }}
                className="flex items-center justify-center gap-2 h-11 px-4 min-w-[100px] text-card-foreground bg-transparent"
              >
                <CirclePlus className="h-5 w-5" />
                Add Condition to Group {groupIndex + 1}
              </Button>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
}
